import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { OAUTH_AUTHORIZE_PAGE, type OAuthRequestView } from '@gg/shared';
import { Button, buttonClasses, Card, Notice, PageHeader } from '@/components/ui';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { ApiRequestError } from '@/lib/api';
import { useAdminMe, useOAuthDecision, useOAuthRequest } from '@/lib/queries';

/** Problems `GET /oauth/authorize` reports here instead of redirecting (`routes/oauth.ts`). */
const KNOWN_ERRORS = ['invalid_request', 'invalid_client', 'invalid_redirect_uri', 'rate_limited'];

/**
 * Consent for an MCP client (issue #47): the authorization step is the back-office login. A
 * visitor signs in with the usual magic link, which returns here (same browser); a signed-in
 * organiser sees the client and approves or denies. The API sends the browser back to the
 * client either way.
 */
export function OAuthAuthorize() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const id = params.get('request');
  const error = params.get('error');
  const request = useOAuthRequest(error ? null : id);

  if (error || !id) {
    const key = error && KNOWN_ERRORS.includes(error) ? error : 'generic';
    return (
      <Shell>
        <PageHeader title={t('oauth.errorTitle')} intro={t(`oauth.error.${key}`)} />
      </Shell>
    );
  }
  if (request.isLoading) return <p className="pt-14 text-center text-ink-muted">…</p>;
  if (!request.data) {
    return (
      <Shell>
        <PageHeader title={t('oauth.errorTitle')} intro={t('oauth.error.generic')} />
      </Shell>
    );
  }
  return <Consent view={request.data} />;
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col pt-14">
      {children}
      <div className="mt-auto pb-8 pt-7 text-center text-sm">
        <LanguageSwitch />
      </div>
    </div>
  );
}

function Consent({ view }: { view: OAuthRequestView }) {
  const { t } = useTranslation();
  const me = useAdminMe();
  const decide = useOAuthDecision();
  const client = view.clientName;
  const status = me.error instanceof ApiRequestError ? me.error.status : null;
  const header = (
    <PageHeader
      eyebrow={view.eventName}
      title={t('oauth.title', { client })}
      intro={t('oauth.intro', { client, host: view.redirectHost })}
    />
  );

  if (view.status !== 'pending') {
    return (
      <Shell>
        {header}
        <div className="pt-7">
          <Notice>{t(`oauth.status.${view.status}`, { client })}</Notice>
        </div>
      </Shell>
    );
  }
  if (!view.sameBrowser) {
    // Only the browser that started the connection may approve it: a link received from
    // someone else is never a way in (decision 0030 D11).
    return (
      <Shell>
        {header}
        <div className="pt-7">
          <Notice>{t('oauth.otherBrowser', { client })}</Notice>
        </div>
      </Shell>
    );
  }
  if (me.isLoading) return <p className="pt-14 text-center text-ink-muted">…</p>;

  const go = async (approve: boolean) => {
    try {
      const { redirectTo } = await decide.mutateAsync({ id: view.id, approve });
      window.location.assign(redirectTo);
    } catch {
      // Shown through decide.isError.
    }
  };
  const redirecting = decide.isSuccess;

  if (status === 401) {
    const back = `${OAUTH_AUTHORIZE_PAGE}?request=${view.id}`;
    return (
      <Shell>
        {header}
        <div className="flex flex-col gap-4 pt-7">
          <p className="m-0">{t('oauth.signIn')}</p>
          <Link to={`/login?redirect=${encodeURIComponent(back)}`} className={buttonClasses()}>
            {t('oauth.signInButton')}
          </Link>
          <p className="m-0 text-sm text-ink-muted">{t('login.sameBrowser')}</p>
        </div>
      </Shell>
    );
  }

  if (!me.data) {
    // 403: signed in, but not on the event's admin allow-list.
    return (
      <Shell>
        {header}
        <div className="flex flex-col gap-4 pt-7">
          <Notice>{t('oauth.notAdmin')}</Notice>
          <Button
            type="button"
            variant="secondary"
            loading={decide.isPending || redirecting}
            onClick={() => go(false)}
          >
            {t('oauth.deny')}
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {header}
      <div className="flex flex-col gap-4 pt-7">
        <Card className="gap-3!">
          <p className="m-0 font-semibold">{t('oauth.access', { email: me.data.email })}</p>
          <p className="m-0 text-sm">{t('oauth.scope', { client })}</p>
          <p className="m-0 text-sm text-ink-muted">{t('oauth.revoke')}</p>
        </Card>
        {view.loopback && <Notice>{t('oauth.loopback', { host: view.redirectHost })}</Notice>}
        <Button
          type="button"
          loading={decide.isPending || redirecting}
          disabled={decide.isPending || redirecting}
          onClick={() => go(true)}
        >
          {t('oauth.approve')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={decide.isPending || redirecting}
          onClick={() => go(false)}
        >
          {t('oauth.deny')}
        </Button>
        {decide.isError && (
          <p role="alert" className="m-0 text-sm text-danger">
            {t('oauth.decisionError', { client })}
          </p>
        )}
        {redirecting && (
          <p role="status" className="m-0 text-center text-sm text-ink-muted">
            {t('oauth.redirecting', { client })}
          </p>
        )}
      </div>
    </Shell>
  );
}
